import * as React from 'react';
import type { FC, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { use$ } from '@legendapp/state/react';

import { setupObserver } from '../option-selector/utils';

import { rootStore } from '../../store';
import TrimlineDropdownComponent from './trimline-dropdown-component';

interface TrimlineDropdownComponentProps {
	testId: string;
	title: string;
}

const LABEL_SELECT_PADDINGS = 56;
const ELLIPSIS = '...';
const ELLIPSIS_LEN = ELLIPSIS.length;

const TrimlineDropDownContainer: FC<TrimlineDropdownComponentProps> = ({ testId, title }) => {
	const [charsToTruncate, setCharsToTruncate] = useState<{ variantName: number; carlineName: number }>({
		variantName: 0,
		carlineName: 0
	});

	const [_, currentModelValue] = use$(rootStore.currentModel);
	const trimName = currentModelValue.vehicleConfiguration?.trimName;

	const [dropdownRect, setDropdownRect] = useState<DOMRect>({ width: 0 } as DOMRect);
	const [dumbVariantNameSpanRect, setDumbVariantNameSpanRect] = useState<DOMRect>({ width: 0 } as DOMRect);
	const [dumbLabelSpanRect, setDumbLabelSpanRect] = useState<DOMRect>({ width: 0 } as DOMRect);

	const dumbVariantNameSpanRef = useRef<HTMLSpanElement>(null);
	const dumbLabelSpanRef = useRef<HTMLSpanElement>(null);
	const trimlineSelectionDropdownRef = useRef<HTMLDivElement>(null);

	const getTruncatedLabel = (label?: string) => {
		const fullLabel = label || '';
		const charsToRemove = label ? charsToTruncate.carlineName : charsToTruncate.variantName;
		if (charsToRemove <= 0) return fullLabel;
		const middle = Math.ceil(fullLabel.length / 2);
		const sideChars = Math.ceil(charsToRemove / 2);
		return `${fullLabel.substring(0, middle - sideChars).trim()}${ELLIPSIS}${fullLabel.substring(middle + sideChars).trim()}`;
	};

	const setupObservers = (refs: RefObject<HTMLElement>[], callbacks: ((rect: DOMRect) => void)[]) => {
		// @TODO type fix!!!
		const observers = refs.map((ref, index) => (ref.current ? setupObserver(ref, callbacks[index]!) : null));
		return () => observers.forEach(observer => observer?.disconnect());
	};

	useEffect(() => {
		return setupObservers(
			[dumbVariantNameSpanRef, dumbLabelSpanRef, trimlineSelectionDropdownRef],
			[setDumbVariantNameSpanRect, setDumbLabelSpanRect, setDropdownRect]
		);
	}, []);

	useEffect(() => {
		if (!dropdownRect.width) return;

		const calculateCharsToTruncate = (labelLength: number, spanWidth: number) => {
			if (!spanWidth) return 0;
			const selectTextWidth = dropdownRect.width - LABEL_SELECT_PADDINGS;
			if (selectTextWidth < LABEL_SELECT_PADDINGS) return 0;
			const charsToFit = Math.floor((selectTextWidth * labelLength) / spanWidth);
			return Math.max(labelLength - charsToFit + ELLIPSIS_LEN, 0);
		};

		setCharsToTruncate({
			variantName: calculateCharsToTruncate(trimName?.length || 0, dumbVariantNameSpanRect.width),
			carlineName: calculateCharsToTruncate(title.length, dumbLabelSpanRect.width)
		});
	}, [dropdownRect.width, dumbVariantNameSpanRect.width, dumbLabelSpanRect.width, trimName, title]);

	return (
		<TrimlineDropdownComponent
			trimlineSelectionDropdownRef={trimlineSelectionDropdownRef}
			testId={testId}
			getTruncatedLabel={getTruncatedLabel}
			dumbVariantNameSpanRef={dumbVariantNameSpanRef}
			dumbLabelSpanRef={dumbLabelSpanRef}
			charsToTruncate={charsToTruncate.variantName}
		/>
	);
};

export default TrimlineDropDownContainer;
